import { Close } from '@mui/icons-material';
import ListIcon from '@mui/icons-material/List';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Menu,
	MenuItem,
	Switch,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuModel } from '~/model/model.menu';
import { FeedbackReplyDto } from '~/shared/dto/admin/feedback.dto';
import { Form } from '~/shared/form';
import { FieldNumber } from '~/view/fields/field.number';
import { FieldText } from '~/view/fields/field.text';
import { AsyncView } from '../view.async';
import { Col, Row, Text } from '../view.box';
import { Image } from '../view.image';
import { useFeedbackDrawerContext } from './feedback.drawer.ctx';

export const FeedbackReplyDialog = observer(() => {
	const {
		async,
		selectedItem,
		similarBrand,
		similarFragrance,
		replyTitleMenu,
		replyMessageMenu,
		replyDialogVisible,
		replyToSelected,
		hideReplyDialog,
	} = useFeedbackDrawerContext();

	const form = Form.use(FeedbackReplyDto, {
		name: 'FeedbackReplyDto',
		onSubmit: replyToSelected,
	});

	const [t] = useTranslation('feedbackReply');
	const titles = t('titles', { returnObjects: true }) as Array<string>;
	const messages = t('messages', { returnObjects: true }) as Array<string>;
	const templateParams = useMemo(() => {
		const params: Record<string, string> = {};
		if (selectedItem) params.userName = selectedItem.createdByName;
		if (selectedItem?.messageParams) Object.assign(params, selectedItem.messageParams);
		if (similarFragrance || similarBrand) {
			params.fragranceName = similarFragrance?.name || '';
			params.brandName = (similarFragrance?.brandName || similarBrand?.name)!;
		}
		return params;
	}, [selectedItem, similarFragrance, similarBrand]);

	useEffect(() => {
		form.reset();
		form.apply({
			imgRight: similarFragrance?.image || similarBrand?.image,
			brandId: similarBrand?.id,
			fragranceId: similarFragrance?.id,
		});
	}, [form, similarFragrance, similarBrand]);

	return (
		<Dialog open={replyDialogVisible} fullWidth maxWidth="md">
			<AsyncView async={async}>
				<DialogTitle>
					<Row justifyContent="space-between">
						<Text>Reply to feedback</Text>
						<IconButton onClick={hideReplyDialog}>
							<Close />
						</IconButton>
					</Row>
				</DialogTitle>
				<DialogContent>
					<Col gap={2}>
						<Row gap={1}>
							<FieldText field={form.$title} />
							<TemplateMenu
								menuModel={replyTitleMenu}
								items={titles}
								onSelect={value => form.$title.set(t(value, templateParams))}
							/>
						</Row>
						<Row gap={1}>
							<FieldText multiline field={form.$message} />
							<TemplateMenu
								menuModel={replyMessageMenu}
								items={messages}
								onSelect={value => form.$message.set(t(value, templateParams))}
							/>
						</Row>
						<Row gap={1}>
							<FieldText field={form.$imgLeft} />
							<Image src={form.$imgLeft.value} />
						</Row>
						<Row gap={1}>
							<FieldText field={form.$imgRight} />
							<Image src={form.$imgRight.value} />
						</Row>
						{selectedItem?.type === 'FRAG_SUGGESTION' && <FieldNumber field={form.$brandId} />}
						{selectedItem?.type === 'FRAG_SUGGESTION' && <FieldNumber field={form.$fragranceId} />}
						<FieldText multiline field={form.$description} />
						<FieldText field={form.$analyticsLabel} />
						<FormControlLabel
							label="Mark feedback as DONE"
							control={
								<Switch
									checked={!!form.$status.value}
									onChange={e => form.$status.set(e.target.checked ? 'DONE' : undefined)}
								/>
							}
						/>
					</Col>
				</DialogContent>
				<DialogActions>
					<Button onClick={hideReplyDialog}>Cancel</Button>
					<Button variant="contained" onClick={form.submit}>
						Send
					</Button>
				</DialogActions>
			</AsyncView>
		</Dialog>
	);
});

type TemplateMenuProps = {
	menuModel: MenuModel;
	items: Array<string>;
	onSelect: (value: string) => void;
};

const TemplateMenu: FC<TemplateMenuProps> = observer(({ menuModel, items, onSelect }) => {
	return (
		<>
			<IconButton onClick={menuModel.toggleMenu}>
				<ListIcon />
			</IconButton>
			<Menu
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				PaperProps={{ sx: { maxWidth: 800 } }}
				open={menuModel.open}
				anchorEl={menuModel.anchorEl}
				onClose={menuModel.closeMenu}
			>
				{items.map((item, index: number) => (
					<MenuItem
						key={index}
						value={index}
						onClick={() => {
							onSelect(item);
							menuModel.closeMenu();
						}}
					>
						<Text noWrap>{item}</Text>
					</MenuItem>
				))}
			</Menu>
		</>
	);
});
